/**
 * The Grid pane — 2019-II-1 rendered as the table it is.
 *
 * COLUMNS, NOT ROWS, and that is the whole layout decision. Sixteen rounds plus Undrafted read as
 * a tall thin strip if they run down one column, and as a wall if they run across. Six per column
 * on a desktop and nine on a phone puts the whole rule on one screen at both sizes, which is what
 * makes it a table you can look UP something in rather than one you have to scroll.
 *
 * `grid-auto-flow: column` is what does it: the cells are written in round order and the browser
 * fills each column before starting the next, so the DOM order is still 1, 2, 3 … for a screen
 * reader and for anybody copying it out.
 *
 * THE UNDRAFTED ROW IS NOT A SEVENTEENTH ROUND. A free agent picked up in season carries the
 * 17th-round value — `udfaRoundValue`, which is `draftRounds + 1` — and the round-16 floor prices
 * everything at or beyond 16 at the 12th. So he lands on the 12th too, and the row says so rather
 * than leaving the reader to work out why a player who was never drafted costs the same as a
 * sixteenth-rounder.
 */

import { useEffect, useMemo, useState } from 'react';
import { LEAGUE_2026 } from '../domain/config.js';
import {
  NOT_ELIGIBLE, RULEBOOK_GRID, type PenaltyRound, escalationChain, penaltyRound, udfaRoundValue,
} from '../domain/grid.js';
import { OverrideError, type Override, inForce, makeOverride } from '../domain/overrides.js';
import { atLeast, type LeagueStore, type Role } from '../store/types.js';
import { Citations } from './RuleText.js';

const SEASON = 2026;
const ROUNDS = LEAGUE_2026.draftRounds;          // 16
const UDFA = udfaRoundValue(LEAGUE_2026);        // 17 — the synthetic round a pickup carries

const ordinal = (n: number): string => {
  const t = n % 100;
  if (t >= 11 && t <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
};

/** What a round costs, as the cell reads it. `null` is the rule's own "not eligible". */
const costLabel = (p: PenaltyRound): string =>
  (p === NOT_ELIGIBLE ? 'not eligible' : `${ordinal(p)} round`);

export function GridPane({ role, store }: { role: Role; store: LeagueStore }) {
  const [overrides, setOverrides] = useState<Override[]>([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<number, string>>({});
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const commish = atLeast(role, 'commissioner');

  useEffect(() => {
    let live = true;
    const load = () => store.overrides(SEASON).then((x) => { if (live) setOverrides(x); });
    void load();
    const off = store.subscribe(SEASON, (what) => { if (what === 'overrides') void load(); });
    return () => { live = false; off(); };
  }, [store]);

  /*
   * What is actually in force, round by round: the rulebook's value unless a commissioner has
   * written one over it. `inForce` filters out reverted rows, so a revert restores the rule
   * without deleting the history of having deviated from it — which is the whole point of the
   * override log.
   */
  const live = useMemo(() => {
    const set = new Map<number, string>();
    for (const o of inForce(overrides)) {
      const m = /^grid:(\d+)$/.exec(o.target);
      if (m) set.set(Number(m[1]), o.setTo);
    }
    return set;
  }, [overrides]);

  const cells = useMemo(() => Array.from({ length: ROUNDS }, (_, i) => {
    const round = i + 1;
    const ruleValue = penaltyRound(round);
    const written = live.get(round);
    return { round, ruleValue, written };
  }), [live]);

  async function save() {
    setErr(null);
    const now = new Date().toISOString();
    const changed = Object.entries(draft)
      .map(([k, v]) => ({ round: Number(k), setTo: v.trim() }))
      .filter((c) => c.setTo !== '');
    try {
      for (const c of changed) {
        const ruleValue = String(penaltyRound(c.round) ?? 'not eligible');
        if (c.setTo === ruleValue) continue;   // a no-op is not an override — `makeOverride` agrees
        await store.saveOverride(makeOverride({
          target: `grid:${c.round}`,
          label: `The Grid — round ${c.round}`,
          ruleValue,
          setTo: c.setTo,
          reason,
          cites: ['2019-II-1'],
        }, 'COMMISH', SEASON, now, `grid-${c.round}-${now}`));
      }
      setEditing(false); setDraft({}); setReason('');
    } catch (e) {
      setErr(e instanceof OverrideError ? e.message : 'That did not save.');
    }
  }

  /*
   * RESET IS A REVERT, NOT A DELETE. `grid.ts` exports `RULEBOOK_GRID` for exactly this — the
   * frozen transcription of 2019-II-1 — and every deviation is marked reverted rather than removed
   * so the log still says the league once did something else.
   */
  async function resetAll() {
    const now = new Date().toISOString();
    for (const o of inForce(overrides).filter((x) => x.target.startsWith('grid:'))) {
      await store.saveOverride({ ...o, revertedAt: now, revertedBy: 'COMMISH' });
    }
    setEditing(false); setDraft({}); setReason('');
  }

  const deviations = inForce(overrides).filter((o) => o.target.startsWith('grid:'));

  return (
    <section className="gridpane">
      <p className="gridintro">
        What it costs to keep a player, by the round you drafted him in. Read down a column.
        <Citations ids={['2019-II-1']} />
      </p>

      <ol className="gridcols">
        {cells.map((c) => {
          const shown = c.written ?? String(c.ruleValue ?? 'not eligible');
          const off = c.written !== undefined;
          const none = c.ruleValue === NOT_ELIGIBLE && !off;
          return (
            <li key={c.round} className={`gridcell ${none ? 'none' : ''} ${off ? 'written' : ''}`}>
              <span className="gcround">Round {c.round}</span>
              <span className="gccost">
                {off ? shown : costLabel(c.ruleValue)}
              </span>
              {off && <span className="gcwas">rulebook: {costLabel(c.ruleValue)}</span>}
            </li>
          );
        })}
        {/* The seventeenth value, named for what it is rather than for its number. */}
        <li className="gridcell udfa">
          <span className="gcround">Undrafted</span>
          <span className="gccost">{costLabel(penaltyRound(UDFA))}</span>
          <span className="gcwas">picked up in season · counts as the {ordinal(UDFA)}</span>
        </li>
      </ol>

      <Walker />

      {deviations.length > 0 && (
        <p className="note warn gridoff">
          {deviations.length} round{deviations.length === 1 ? ' is' : 's are'} set away from the
          rulebook this season.
        </p>
      )}

      {commish && !editing && (
        <div className="gridacts">
          <button type="button" className="btn small" onClick={() => setEditing(true)}>
            Edit the Grid
          </button>
          <button type="button" className="linkbtn tiny" disabled={deviations.length === 0}
            onClick={() => void resetAll()}>
            Reset to the rulebook
          </button>
        </div>
      )}

      {commish && editing && (
        <div className="card gated commish grideditor">
          <p className="mut small">
            Leave a round blank to keep the rulebook’s value. Every change is logged against
            2019-II-1 with your reason, and can be reverted.
          </p>
          <div className="gridinputs">
            {cells.map((c) => (
              <label key={c.round} className="gridinput">
                <span className="mut">R{c.round}</span>
                <input value={draft[c.round] ?? ''} inputMode="numeric"
                  placeholder={String(c.ruleValue ?? '—')}
                  onChange={(e) => setDraft((d) => ({ ...d, [c.round]: e.target.value }))} />
              </label>
            ))}
          </div>
          <label className="gridreason">
            <span className="mut">Why</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="An override needs a reason — it is the only part anybody reads later." />
          </label>
          {err && <p className="note bad tight">{err}</p>}
          <div className="gridacts">
            <button type="button" className="btn primary" disabled={!reason.trim()}
              onClick={() => void save()}>Save the changes</button>
            <button type="button" className="linkbtn"
              onClick={() => { setEditing(false); setDraft({}); setErr(null); }}>cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * One row of the Grid, walked to the end.
 *
 * The table is the whole rule; this is the only form in which "four keeper years" means anything,
 * because the cost escalates each time you keep him and the chain is what runs out.
 */
function Walker() {
  const [from, setFrom] = useState<number>(ROUNDS);
  const chain = escalationChain(from);
  return (
    <div className="gridwalk">
      <label className="inline">
        <span className="mut">Keep a player drafted in</span>
        <select value={from} onChange={(e) => setFrom(Number(e.target.value))}>
          {Array.from({ length: ROUNDS }, (_, i) => i + 1).map((r) => (
            <option key={r} value={r}>Round {r}</option>
          ))}
          <option value={UDFA}>Undrafted</option>
        </select>
      </label>
      <ol className="gridchain">
        {chain.map((r, i) => (
          <li key={`${r}-${i}`}><span className="gcstep">{ordinal(r)}</span></li>
        ))}
        <li className="out"><span className="gcstep">out</span></li>
      </ol>
      <p className="mut small">
        {chain.length} keeper year{chain.length === 1 ? '' : 's'} before the escalation runs out.
        {' '}The rulebook’s own value for this round is {costLabel(RULEBOOK_GRID[from] ?? null)}.
      </p>
    </div>
  );
}
